import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Armchair,
  Monitor,
  UserCheck,
  Wrench,
  Trash2,
  Package,
  TrendingUp,
  MapPin,
  AlertCircle,
  Plus,
  ArrowRight,
  Loader2
} from "lucide-react";

export default function MobiliarioDashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/mobiliario/dashboard/stats`, getAuthHeaders());
      setStats(response.data);
    } catch (err) {
      console.error("Error loading stats:", err);
      toast.error("Error al cargar estadísticas");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="mobiliario-dashboard-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Inventario Mobiliario
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Control y gestión de activos fijos de oficina
            </p>
          </div>
          <Button 
            onClick={() => navigate("/mobiliario/activos")}
            className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Activo
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="kpi-carousel grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          <Card className="border-blue-200 bg-blue-50/50 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/mobiliario/activos")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                  <Package className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700">{stats?.total_activos || 0}</p>
                  <p className="text-sm text-blue-600">Total Activos</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-green-50/50 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/mobiliario/activos?estado=disponible")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center">
                  <Monitor className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-700">{stats?.disponibles || 0}</p>
                  <p className="text-sm text-green-600">Disponibles</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-purple-200 bg-purple-50/50 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/mobiliario/asignaciones")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500 flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-700">{stats?.asignados || 0}</p>
                  <p className="text-sm text-purple-600">Asignados</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-amber-200 bg-amber-50/50 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/mobiliario/mantenimiento")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700">{stats?.en_mantenimiento || 0}</p>
                  <p className="text-sm text-amber-600">Mantenimiento</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-200 bg-red-50/50 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/mobiliario/bajas")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-700">{stats?.dados_baja || 0}</p>
                  <p className="text-sm text-red-600">Dados de Baja</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Value and Alerts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Total Value Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <TrendingUp className="w-5 h-5 text-[#0C314C]" />
                Valor Total del Inventario
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-[#0C314C]">
                {formatCurrency(stats?.valor_total)}
              </p>
              <p className="text-sm text-slate-500 mt-2">
                Valor de adquisición de todos los activos (excluyendo bajas)
              </p>
            </CardContent>
          </Card>

          {/* Alerts Card */}
          <Card className={stats?.mantenimiento_pendiente > 0 ? "border-amber-300" : ""}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Alertas
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.mantenimiento_pendiente > 0 ? (
                <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-200">
                      {stats.mantenimiento_pendiente} mantenimientos pendientes
                    </p>
                    <p className="text-sm text-amber-600 dark:text-amber-400">
                      Requieren atención
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate("/mobiliario/mantenimiento")}
                    className="border-amber-500 text-amber-600"
                  >
                    Ver
                  </Button>
                </div>
              ) : (
                <p className="text-slate-500">No hay alertas pendientes</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Categories and Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* By Category */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Armchair className="w-5 h-5 text-[#0C314C]" />
                Por Categoría
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.por_categoria?.length > 0 ? (
                <div className="space-y-3">
                  {stats.por_categoria.slice(0, 6).map((cat, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: cat.color }}
                        />
                        <span className="text-slate-700 dark:text-slate-300">{cat.nombre}</span>
                      </div>
                      <Badge variant="outline">{cat.count}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500">No hay categorías configuradas</p>
              )}
              <Button 
                variant="ghost" 
                className="w-full mt-4"
                onClick={() => navigate("/mobiliario/categorias")}
              >
                Ver todas las categorías
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <MapPin className="w-5 h-5 text-[#0C314C]" />
                Acciones Rápidas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate("/mobiliario/activos")}
              >
                <Monitor className="w-4 h-4 mr-2" />
                Gestionar Activos
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate("/mobiliario/asignaciones")}
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Nueva Asignación
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate("/mobiliario/mantenimiento")}
              >
                <Wrench className="w-4 h-4 mr-2" />
                Programar Mantenimiento
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate("/mobiliario/ubicaciones")}
              >
                <MapPin className="w-4 h-4 mr-2" />
                Gestionar Ubicaciones
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
